import React, { useState, useEffect, useCallback, useContext } from 'react';
import { ArrowLeft, ArrowDownUp, CreditCard, Search, Filter, ChevronDown, Calendar, Check } from 'lucide-react';
import AppContext from '../../context/AppContext';
import { useNavigation } from '../../context/NavigationContext';
import { database, ref, get } from "../../FirebaseConfig";

// Composant pour afficher un élément de transaction
const TransactionItem = ({ transaction, onSelect }) => {
  const isIncome = transaction.type === 'income';
  
  return (
    <div 
      className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-xl transition-colors cursor-pointer"
      onClick={() => onSelect(transaction)}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-full ${isIncome ? 'bg-green-100' : 'bg-blue-100'}`}>
          {isIncome ? (
            <ArrowDownUp className="w-5 h-5 text-green-600" />
          ) : (
            <CreditCard className="w-5 h-5 text-blue-600" />
          )}
        </div>
        <div>
          <p className="font-medium text-gray-800">
            {transaction.nomClient || (isIncome ? 'Réception' : 'Paiement')}
          </p>
          <p className="text-xs text-gray-500">{transaction.date}</p>
        </div>
      </div>
      <div className="text-right">
        <p className={`font-bold ${isIncome ? 'text-green-600' : 'text-gray-800'}`}>
          {isIncome ? '+' : '-'}{transaction.amount} FCFA
        </p>
        <p className={`text-xs ${
          transaction.status === 'Complété' ? 'text-green-600' : 
          transaction.status === 'En attente' ? 'text-orange-500' : 'text-red-500'
        }`}>
          {transaction.status}
        </p>
      </div>
    </div>
  );
};

// Composant de détail d'une transaction
const TransactionDetail = ({ transaction, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-40 flex items-center justify-center backdrop-blur-sm">
      <div className="bg-white rounded-xl w-full max-w-md mx-4 overflow-hidden animate-fadeIn">
        <div className="bg-blue-600 text-white p-4">
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={onClose}
              className="p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold">Détails de la transaction</h2>
            <div className="w-9"></div> {/* Spacer pour centrer le titre */}
          </div>
          
          <div className="mt-2 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-full bg-white bg-opacity-20">
                <ArrowDownUp className="w-6 h-6" />
              </div>
              <p className="font-medium">{transaction.type === 'income' ? 'Paiement reçu' : 'Paiement envoyé'}</p>
            </div>
            <p className="text-xl font-bold">{transaction.amount} FCFA</p>
          </div>
        </div>
        
        <div className="p-4 divide-y">
          <div className="py-3">
            <p className="text-gray-500 text-sm mb-1">Statut</p>
            <div className="flex items-center">
              <div className={`w-3 h-3 rounded-full mr-2 ${
                transaction.status === 'Complété' ? 'bg-green-500' : 
                transaction.status === 'En attente' ? 'bg-orange-500' : 'bg-red-500'
              }`}></div>
              <p className="font-medium">{transaction.status}</p>
            </div>
          </div>
          
          <div className="py-3">
            <p className="text-gray-500 text-sm mb-1">ID Transaction</p>
            <p className="font-medium break-all">{transaction.id}</p>
          </div>
          
          <div className="py-3">
            <p className="text-gray-500 text-sm mb-1">Date</p>
            <p className="font-medium">{transaction.date}</p>
          </div>
          
          {transaction.idProduit && (
            <div className="py-3">
              <p className="text-gray-500 text-sm mb-1">Produit</p>
              <p className="font-medium">{transaction.produitNom || transaction.idProduit}</p>
            </div>
          )}
          
          {transaction.idClient && (
            <div className="py-3">
              <p className="text-gray-500 text-sm mb-1">Client</p>
              <p className="font-medium">{transaction.nomClient || transaction.idClient}</p>
            </div>
          )}
          
          {transaction.duration && (
            <div className="py-3">
              <p className="text-gray-500 text-sm mb-1">Durée</p>
              <p className="font-medium">{transaction.duration}</p>
            </div>
          )}
        </div>
        
        <div className="p-4 bg-gray-50">
          <button 
            onClick={onClose}
            className="w-full py-3 text-center bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

// Filtres pour les transactions
const FilterPanel = ({ filters, onFilterChange, isOpen, onToggle }) => {
  return (
    <div className="mb-4">
      <button 
        onClick={onToggle}
        className="flex items-center justify-between w-full bg-white p-3 rounded-xl shadow-sm"
      >
        <div className="flex items-center gap-2 text-gray-700">
          <Filter className="w-5 h-5" />
          <span>Filtres</span>
        </div>
        <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="mt-3 bg-white p-4 rounded-xl shadow-sm animate-fadeIn">
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Statut</p>
            <div className="flex flex-wrap gap-2">
              {['Tous', 'Complété', 'En attente', 'Échec'].map(status => (
                <button
                  key={status}
                  className={`px-3 py-1.5 rounded-full text-sm ${
                    filters.status === status || (status === 'Tous' && !filters.status)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => onFilterChange('status', status === 'Tous' ? '' : status)}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
          
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Période</p>
            <div className="flex flex-wrap gap-2">
              {['Tous', 'Aujourd\'hui', 'Cette semaine', 'Ce mois'].map(period => (
                <button
                  key={period}
                  className={`px-3 py-1.5 rounded-full text-sm ${
                    filters.period === period || (period === 'Tous' && !filters.period)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => onFilterChange('period', period === 'Tous' ? '' : period)}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={() => {
                onFilterChange('status', '');
                onFilterChange('period', '');
              }}
              className="px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Composant principal
const TransactionHistory = () => {
  const { id } = useContext(AppContext);
  const { goBack } = useNavigation(); // Utiliser le contexte de navigation
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({ status: '', period: '' });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  
  // Fonction pour charger et traiter les données de transaction
  const loadTransactionData = useCallback(async () => {
    if (!id) return [];
    
    setLoading(true);
    
    try {
      // Référence directe à transaction-abonnement dans Firebase
      const transactionRef = ref(database, `transaction-abonnement`);
      const snapshot = await get(transactionRef);
      
      if (!snapshot.exists()) {
        setLoading(false);
        return [];
      }
      
      const transactionData = snapshot.val();
      const transactionsArray = [];
      
      // Parcourir l'objet transaction-abonnement
      Object.entries(transactionData).forEach(([transactionId, transaction]) => {
        // Ne conserver que les transactions où l'idVendeur correspond à l'utilisateur connecté
        if (transaction.idVendeur && transaction.idVendeur.toString() === id?.toString()) {
          transactionsArray.push({
            id: transactionId,
            amount: transaction.amount?.toLocaleString() || '0',
            type: 'income', // Toutes les transactions sont des revenus pour le vendeur
            date: transaction.created_at ? new Date(transaction.created_at).toLocaleDateString('fr-FR', {
              day: '2-digit', 
              month: 'short', 
              year: 'numeric'
            }) : 'Date inconnue',
            dateObj: transaction.created_at ? new Date(transaction.created_at) : new Date(0),
            status: transaction.status === 'success' ? 'Complété' : 
                    transaction.status === 'pending' ? 'En attente' : 'Échec',
            idProduit: transaction.idProduit,
            idClient: transaction.idClient,
            duration: transaction.duration,
            // Ajouter d'autres propriétés utiles
            rawData: transaction
          });
        }
      });
      
      // Trier par date décroissante (plus récent d'abord)
      transactionsArray.sort((a, b) => b.dateObj - a.dateObj);
      
      setTransactions(transactionsArray);
      setLoading(false);
      return transactionsArray;
    } catch (error) {
      console.error("Erreur lors du chargement des transactions:", error);
      setLoading(false);
      return [];
    }
  }, [id]);
  
  // Charger les transactions au montage du composant
  useEffect(() => {
    loadTransactionData();
  }, [loadTransactionData]);
  
  // Le bouton retour est géré par le NavigationContext
  // Nous n'avons plus besoin de gérer le bouton retour de Telegram ici
  
  // Appliquer les filtres et recherche
  const filteredTransactions = transactions.filter(transaction => {
    // Filtrer par recherche
    const searchMatch = !searchTerm || 
      transaction.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.amount.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (transaction.nomClient && transaction.nomClient.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Filtrer par statut
    const statusMatch = !filters.status || transaction.status === filters.status;
    
    // Filtrer par période
    let periodMatch = true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (filters.period) {
      const transactionDate = transaction.dateObj;
      
      switch(filters.period) {
        case 'Aujourd\'hui':
          periodMatch = transactionDate >= today;
          break;
        case 'Cette semaine': {
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          periodMatch = transactionDate >= startOfWeek;
          break;
        }
        case 'Ce mois': {
          const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          periodMatch = transactionDate >= startOfMonth;
          break;
        }
        default:
          periodMatch = true;
      }
    }
    
    return searchMatch && statusMatch && periodMatch;
  });
  
  // Mettre à jour un filtre
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };
  
  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-blue-600 text-white">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <button 
              onClick={goBack}
              className="p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold">Historique des transactions</h1>
            <div className="w-9"></div> {/* Spacer pour équilibrer */}
          </div>
          
          {/* Barre de recherche */}
          <div className="mb-4 relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher une transaction..."
              className="w-full bg-white bg-opacity-10 border-none text-white placeholder-white placeholder-opacity-70 py-3 pl-11 pr-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-white"
            />
            <Search className="w-5 h-5 text-white absolute left-4 top-1/2 transform -translate-y-1/2" />
          </div>
        </div>
      </div>
      
      <div className="max-w-lg mx-auto px-4 py-4">
        {/* Panneau de filtres */}
        <FilterPanel 
          filters={filters}
          onFilterChange={handleFilterChange}
          isOpen={filterOpen}
          onToggle={() => setFilterOpen(!filterOpen)}
        />
        
        {/* Liste des transactions */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement des transactions...</p>
            </div>
          ) : filteredTransactions.length > 0 ? (
            <div className="divide-y">
              {filteredTransactions.map((transaction) => (
                <TransactionItem 
                  key={transaction.id} 
                  transaction={transaction}
                  onSelect={() => setSelectedTransaction(transaction)}
                />
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <Calendar className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm || filters.status || filters.period 
                  ? "Aucune transaction trouvée" 
                  : "Aucune transaction"}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {searchTerm || filters.status || filters.period
                  ? "Essayez d'autres critères de recherche"
                  : "Les transactions apparaîtront ici lorsque vous recevrez des paiements"}
              </p>
              {(searchTerm || filters.status || filters.period) && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilters({ status: '', period: '' });
                  }}
                  className="px-4 py-2 text-blue-600 bg-blue-50 rounded-lg font-medium hover:bg-blue-100"
                >
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Modal de détail de transaction */}
      {selectedTransaction && (
        <TransactionDetail 
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
        />
      )}
      
      {/* Styles pour les animations */}
      <style jsx="true">{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default TransactionHistory;